import { CussStatusCode } from '@app/enums/cuss-device-status.enum';
import { CallbackContext } from '@app/cuss/embross/utils/callbacks/ssbd.callback';

/**
 * Superficie mínima que el callback necesita del servicio de papel bajo.
 * Mantenerla aquí evita que esta utilidad conozca servicios o componentes Angular.
 */
export interface BagtagMediaLowHandler {
  notifyMediaLow(): void;
  notifyMediaEmpty(): void;
  notifyMediaRecovered(): void;
}

/**
 * Extrae el código numérico de una respuesta `statusIsOK`, cuyo formato crudo del
 * SDK es `"<status>,<isOK>"` (p. ej. `"107,1"`).
 *
 * @returns el código, o `null` si el valor no es interpretable
 */
export function parseBagtagStatusIsOK(value: unknown): number | null {
  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }
  const code = Number(value.split(',')[0]);
  return Number.isFinite(code) ? code : null;
}

/**
 * Aplica un código de estado del Bag Tag Printer sobre el handler de papel bajo.
 *
 * Solo se interpretan los códigos dentro del alcance de papel de Bag Tag:
 * - `107 MEDIA_LOW`   → inicia el episodio de papel bajo (CI-22506).
 * - `108 MEDIA_EMPTY` → inicia el episodio de papel vacío (CVT20-14314).
 * - `0 OK`            → termina el episodio y limpia cualquier aviso pendiente.
 *
 * Cualquier otro código (101, 301–309, …) se ignora deliberadamente para
 * conservar intacto el comportamiento previo.
 */
export function applyBagtagStatusCode(
  code: number | null,
  handler: BagtagMediaLowHandler | null | undefined
): void {
  if (code === null || !handler) {
    return;
  }
  if (code === CussStatusCode.MEDIA_LOW) {
    handler.notifyMediaLow();
    return;
  }
  if (code === CussStatusCode.MEDIA_EMPTY) {
    handler.notifyMediaEmpty();
    return;
  }
  if (code === CussStatusCode.OK) {
    handler.notifyMediaRecovered();
  }
}

/**
 * Crea el callback compartido del BAGTAG_PRINTER — CI-22506.
 *
 * Sigue el mismo patrón que `createSsbdCallback`: una closure conserva el contexto
 * (no depende de `this`), interpreta `event.key` / `event.value` y delega en un
 * servicio Angular resuelto de forma lazy a través del `CallbackContext`.
 *
 * Alcance estricto: únicamente `107 MEDIA_LOW`, `108 MEDIA_EMPTY` y `0 OK`. No se
 * maneja aquí ningún otro código —incluidos 301–309— para no alterar el
 * comportamiento existente ni duplicar su lógica.
 *
 * El segundo elemento de `event.value` es el flag `fatal` del SDK y no participa
 * en la decisión: solo se usa `event.value[0]`.
 */
export function createBagtagCallback(ctx: CallbackContext) {
  return (event: unknown): void => {
    try {
      const handler = ctx.getBagtagMediaLowService?.();
      if (!handler) {
        return;
      }

      const { key, value } = (event ?? {}) as { key?: unknown; value?: unknown };

      switch (key) {
        case 'statusChange': {
          const raw = Array.isArray(value) ? value[0] : value;
          const code = Number(raw);
          applyBagtagStatusCode(Number.isFinite(code) ? code : null, handler);
          break;
        }

        case 'statusIsOK':
          applyBagtagStatusCode(parseBagtagStatusIsOK(value), handler);
          break;

        default:
          break;
      }
    } catch (err) {
      // Nunca propagar al SDK: una excepción aquí aborta el resto del despacho.
      console.error('[BAGTAG] media-low callback error:', err);
    }
  };
}
